'use client';

import MuiBreadcrumbs from '@mui/material/Breadcrumbs';
import { FC } from 'react';
import { Container } from 'atoms/Container';
import { IconSVG } from 'atoms/IconSVG';
import { Link } from 'atoms/Link/Link';
import { ParseBBCode } from 'atoms/ParseBBCode';
import { CTA } from 'atoms/Typography/Typography';
import { useGtmCategoryPage } from 'modules/gtm/modules/sections/page/hooks';
import { gtmPushPageCategory } from 'modules/gtm/modules/sections/page/page';
import { TKey, useTranslations } from 'modules/i18n/useTranslations';
import { useRouter } from 'modules/next/hooks';
import { AppRoutes } from 'modules/shop/routes';
import { PageType } from 'modules/stroyblok/types';
import styles from './styles.module.css';

type BreadcrumbsProps = {
  items?: Array<BreadCrumbLink>;
  nonStaticPage?: boolean;
  onCurrentItemClick?: () => void;
  pageType?: PageType;
};

export type BreadCrumbLink = {
  href?: string;
  title: string;
};

export const Breadcrumbs: FC<BreadcrumbsProps> = ({
  items,
  nonStaticPage,
  onCurrentItemClick,
  pageType,
}) => {
  const t = useTranslations();
  const router = useRouter();

  useGtmCategoryPage({
    breadcrumbs: items,
    pageType,
    nonStaticPage,
    gtm: { gtmPushPageCategory },
  });

  return (
    <div className={styles.root}>
      <Container>
        <MuiBreadcrumbs
          className={styles.breadcrumbs}
          separator={<IconSVG type="arrowRightBreadcrumbs" />}
          classes={{
            separator: styles.separator,
          }}
        >
          <CTA
            type="md"
            className={styles.link}
            onClick={() => router.push(AppRoutes.HOME)}
          >
            {t('breadcrumbs.home')}
          </CTA>

          {items?.map(({ title, href }, index) => {
            const itemTitle = nonStaticPage
              ? title
              : t(`breadcrumbs.${title}` as TKey);

            const isTheLastItem = index === items.length - 1;

            return !href || isTheLastItem ? (
              <CTA
                key={itemTitle}
                type="md"
                className={onCurrentItemClick ? styles.link : ''}
                onClick={onCurrentItemClick}
              >
                <ParseBBCode content={itemTitle} />
              </CTA>
            ) : (
              <Link key={itemTitle} className={styles.link} href={href}>
                <CTA type="md">{itemTitle}</CTA>
              </Link>
            );
          })}
        </MuiBreadcrumbs>
      </Container>
    </div>
  );
};
